import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Shield, EyeOff, Trash2, Clock, Lock } from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';

const privacyPoints = [
  { icon: EyeOff, title: "Zero Background Tracking", text: "No GPS monitoring or daily routine tracking. Only your target destination is needed." },
  { icon: Trash2, title: "Instant Hard Delete", text: "Your data is permanently wiped the second you click 'Arrived Safely'." },
  { icon: Lock, title: "Unindexed & Private", text: "Emergency links are hidden from search engines and fully protected." },
  { icon: Clock, title: "48-Hour Auto-Destruct", text: "Emergency links automatically expire after 48 hours." },
  { icon: Shield, title: "Secure Architecture", text: "Your emergency instructions and attached media are encrypted and unlocked only if time expires." },
];

export default function PrivacySection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const isMobile = useIsMobile();

  return (
    <section id="privacy" ref={ref} className="relative py-32 md:py-40 bg-[#0a1628] overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 container mx-auto px-6 max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: isMobile ? 0 : 1 }}
            className="relative h-80 md:h-96"
          >
            {/* Central Shield Container */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
              <div className="relative w-32 h-32 md:w-40 md:h-40">
                <div className="absolute inset-0 bg-emerald-500/20 rounded-full blur-2xl animate-pulse-slow" />
                <div className="relative w-full h-full rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center shadow-2xl shadow-emerald-500/10">
                  <Shield className="w-16 h-16 md:w-20 md:h-20 text-emerald-400" />
                </div>
              </div>
            </div>

            {/* Connecting Lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-30 text-emerald-400 z-10">
              <defs>
                <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
                  <stop offset="50%" stopColor="currentColor" stopOpacity="1" />
                  <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                </linearGradient>
              </defs>
              <line x1="20%" y1="20%" x2="50%" y2="50%" stroke="url(#lineGrad)" strokeWidth="1.5" />
              <line x1="80%" y1="25%" x2="50%" y2="50%" stroke="url(#lineGrad)" strokeWidth="1.5" />
              <line x1="25%" y1="75%" x2="50%" y2="50%" stroke="url(#lineGrad)" strokeWidth="1.5" />
              <line x1="80%" y1="80%" x2="50%" y2="50%" stroke="url(#lineGrad)" strokeWidth="1.5" />
            </svg>

            {/* Orbiting Icons */}
            <motion.div 
              className="absolute top-8 left-8 w-14 h-14 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 flex items-center justify-center z-30 shadow-lg"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <EyeOff className="w-6 h-6 text-emerald-400/80" />
            </motion.div>
            <motion.div 
              className="absolute top-12 right-12 w-12 h-12 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 flex items-center justify-center z-30 shadow-lg"
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            >
              <Trash2 className="w-5 h-5 text-emerald-400/80" />
            </motion.div>
            <motion.div 
              className="absolute bottom-12 left-16 w-12 h-12 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 flex items-center justify-center z-30 shadow-lg"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            >
              <Lock className="w-5 h-5 text-emerald-400/80" />
            </motion.div>
            <motion.div 
              className="absolute bottom-8 right-8 w-14 h-14 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 flex items-center justify-center z-30 shadow-lg"
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
            >
              <Clock className="w-6 h-6 text-emerald-400/80" />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: isMobile ? 0 : 1, delay: isMobile ? 0 : 0.2 }}
          >
            <span className="inline-block text-xs font-medium uppercase tracking-[0.2em] text-emerald-400/60 mb-4">
              Your Data, Your Rules
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
              Privacy isn't a<br /><span className="text-emerald-400">feature</span>. It's the foundation.
            </h2>

            <div className="space-y-6">
              {privacyPoints.map((point, i) => (
                <motion.div
                  key={point.title}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: isMobile ? 0 : 0.5, delay: isMobile ? 0 : 0.4 + i * 0.1 }}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/20 transition-colors">
                    <point.icon className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">{point.title}</h3>
                    <p className="text-white/50 text-sm">{point.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
